/**
 * Accepts either a data URL or a blob URL and returns a File object.
 */
async function convertUrlToFile(urlInput: string, filename: string): Promise<File> {
    let url = urlInput;
    if (isBlobUrl(url)) {
        url = await blobUrlToDataURL(url);
    }
    return dataURLtoFile(url, filename);
}

/**
 * Determines whether a given URL is a Blob URL.
 */
function isBlobUrl(url: string): boolean {
    return url.startsWith("blob:");
}

/**
 * Converts a Blob URL to a data URL using FileReader.
 */
async function blobUrlToDataURL(blobUrl: string): Promise<string> {
    try {
        const response = await fetch(blobUrl);
        const blob = await response.blob();

        return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                if (typeof reader.result === 'string') {
                    resolve(reader.result);
                } else {
                    reject("Invalid result from FileReader");
                }
            };
            reader.onerror = () => reject("Error reading blob as data URL");
            reader.readAsDataURL(blob);
        });
    } catch (err) {
        throw new Error(`Failed to fetch or convert blob URL: ${err}`);
    }
}

/**
 * Converts a data URL (base64) to a File object.
 * Optionally allows forcing a MIME type.
 */
function dataURLtoFile(dataurl: string, filename: string): File {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
    const bstr = atob(arr[1]);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) {
        u8arr[i] = bstr.charCodeAt(i);
    }
    return new File([u8arr], filename, { type: mime });
}

export { convertUrlToFile };
